import type { Exam, ExamSchedule, ExamType } from '@mathflat/domain/Exam'
import type { WorksheetReportPdfParams } from '@mathflat/domain/Report'
import type { Workbook } from '@mathflat/domain/Workbook'
import type { WorksheetFileParams } from '@mathflat/domain/Worksheet'
import type { GroupInfo, Report, Student, Worksheet } from 'src/dto'
import WebviewService from 'src/service/webview.service'

import type { AchievementReport } from '@/dto/AchievementAnalysis/AchievementReport.dto.ts'
import type { SimpleReport } from '@/dto/Report/SimpleReport'
import type { WorksheetDownloadPdfType } from '@/types/download'

import type { FileDownloadT } from './download'
import { fileDownload } from './download'

// 앱(안드로이드)에 넘기는 그룹 보고서 산출물 키
type GroupReportExportParams = { key: string }
// 앱(안드로이드)에 넘기는 그룹 학습지 내보내기 옵션. 학습지마다 배정 학생 이름 또는 ID 를 묶어 보낸다
type AssignedWorksheetExportParams = Pick<
  WorksheetFileParams,
  'worksheetPdfTypes' | 'presignedUrlType' | 'isMergedPdf'
> & {
  worksheets: ({ worksheetId: WorksheetFileParams['worksheetIds'][number] } & Pick<
    WorksheetFileParams,
    'studentNames' | 'studentIds'
  >)[]
}

// 새로운 통 웹뷰에서 웹에서 앱 호출할 목록들
/***
 * @see https://www.notion.so/freewheelin/App-Web-thflat-a6a6644147164903b40919120922f73c
 ***/

const appPostMsg = {
  // QR Scan
  scanQR() {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scanQRCode()
    }
  },

  // 네이티브 브라우저 열기
  openBrowser(url: string) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.openBrowser(url)
    }
  },

  exportRemoteFile(url: string, filename: string) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler('exportRemoteFile', JSON.stringify({ url, filename }))
    }
  },

  openDevSetting() {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler('openDevSetting')
    }
  },
  undefinedMsg() {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler('undefinedMsg')
    }
  },

  // 학생 엑셀 다운로드
  exportStudentExcel(studentIds: Student['id'][], filename: string) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface.scriptHandler(
        'exportStudentExcel',
        JSON.stringify({ studentIds, filename }),
      )
    }
  },

  // 원클릭 보고서 다운로드
  downloadOneClickReport(params: WorksheetReportPdfParams & { fileName: string }) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler('downloadOneClickReport', JSON.stringify(params))
    }
  },
  // 원클릭 보고서 미리보기
  previewOneClickReport(params: WorksheetReportPdfParams) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler('previewOneClickReport', JSON.stringify(params))
    }
  },
  // 학습지 미리보기 - 학습지 ID
  previewWorksheet(worksheetId: Worksheet['id'], worksheetPdfType?: WorksheetDownloadPdfType) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler(
        'v2/previewWorksheet',
        JSON.stringify({ worksheetId, worksheetPdfTypes: worksheetPdfType }),
      )
    }
  },
  //학습지 내보내기
  exportWorksheets(params: Omit<WorksheetFileParams, 'presignedUrlType'>) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler('v2/exportWorksheets', JSON.stringify(params))
    }
  },
  // 수업 - 그룹뷰에서 출제된 학습지 내보내기
  exportAssignedWorksheetsForGroup(params: AssignedWorksheetExportParams) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler(
        'v2/exportAssignedWorksheetsForGroup',
        JSON.stringify(params),
      )
    }
  },
  // 시험지 - 결과지, 상장 내보내기
  exportExamReportAndCertificate(examId: Exam['id']) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler(
        'exportExamReportAndCertificate',
        JSON.stringify({ id: examId }),
      )
    }
  },
  // 시험 상장 일괄 내보내기 (zip)
  exportExamCertificates(params: { type: ExamType; round: ExamSchedule['round'] }) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler('v2/exportExamCertificates', JSON.stringify(params))
    }
  },
  // 내신기출 PDF 내보내기
  exportSchoolTests(schoolTestIds: number[]) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler(
        'v2/exportSchoolTests',
        JSON.stringify({ schoolTestIds }),
      )
    }
  },
  // 내신기출 PDF 미리보기
  previewSchoolTests(schoolTestIds: number[]) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler(
        'v2/previewSchoolTests',
        JSON.stringify({ schoolTestIds }),
      )
    }
  },
  // 그룹 원클릭 보고서 내보내기
  exportGroupOneClickReport(params: GroupReportExportParams) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler('v2/exportGroupOneClickReport', JSON.stringify(params))
    }
  },
  // 자동시험 그룹 원클릭 보고서 내보내기
  exportGroupCasOneClickReport(params: GroupReportExportParams) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler(
        'v2/exportGroupCasOneClickReport',
        JSON.stringify(params),
      )
    }
  },
  exportGroupExamOneClickReport(params: GroupReportExportParams) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler(
        'v2/exportGroupExamOneClickReport',
        JSON.stringify(params),
      )
    }
  },
  // 교재 미리보기 - 학습지 ID
  previewWorkbook(workbookId: Workbook['id']) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler('v2/previewWorkbook', JSON.stringify({ workbookId }))
    }
  },
  //교재 내보내기
  exportWorkbook(params: {
    workbookIds: Workbook['id'][]
    fileName: string
    pdfTypes?: WorksheetDownloadPdfType[]
  }) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler('v2/exportWorkbook', JSON.stringify(params))
    }
  },
  previewReport(reportId: Report['id']) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler('v2/previewReport', JSON.stringify({ reportId }))
    }
  },

  previewAchievementReport(reportId: AchievementReport['id']) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler(
        'v2/previewAchievementReport',
        JSON.stringify({ reportId }),
      )
    }
  },

  exportReport(params: { reportIds: SimpleReport['id'][]; groupName: GroupInfo['name'] }) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler('v2/exportReport', JSON.stringify(params))
    }
  },

  exportRealTimeReport(params: { studentId: string; year: number; month: number }) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler('v2/exportRealtimeReport', JSON.stringify(params))
    }
  },

  previewRealtimeReport(params: { studentId: string; year: number; month: number }) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler('v2/previewRealtimeReport', JSON.stringify(params))
    }
  },

  // 웹뷰에서 자바스크립트 함수 호출
  sendJavascript(javascriptFunc: `javascript:${string}`) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler(
        'callFunction',
        JSON.stringify({ scriptCode: javascriptFunc }),
      )
    }
  },
  exportVideoLink(link) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler('exportVideoLink', JSON.stringify({ url: link }))
    }
  },

  // IOS 대응 - IOS 네이티브 개발 이후 제거
  onPreviewIOS(url: string, file: FileDownloadT) {
    if (WebviewService.isIpad) {
      fileDownload(url, file)
    }
  },

  launchSchoolflat() {
    if (WebviewService.isAppAndroid) {
      const isSchoolflatAppInstalled = window.AndroidInterface?.launchSchoolflat()
      return isSchoolflatAppInstalled
    }
    return false
  },

  // 교재 정산
  exportSettlementListExcel(params: { targetDate: string }) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler('exportSettlementList', JSON.stringify(params))
    }
  },

  previewOrderItem(orderItemId: number) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler('v2/previewOrderItem', JSON.stringify({ orderItemId }))
    }
  },

  previewCartItem(cartItemId: string) {
    if (WebviewService.isAppAndroid) {
      window.AndroidInterface?.scriptHandler('v2/previewCartItem', JSON.stringify({ cartItemId }))
    }
  },
}

export default appPostMsg
